// React
import React, { useState } from "react";

// External libraries
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripDotsVertical } from "@fortawesome/sharp-solid-svg-icons";

// Internal
import { ColorPicker } from "../color-picker/color-picker";
import { DatePicker } from "../datepicker/datepicker";
import { getDataTestId } from "../functions/data-test-ids";
import { AppFont } from "../icons/icons";
import { InputNumber } from "../inputs/input-number";
import { InputString } from "../inputs/input-string";
import { InputTime } from "../inputs/input-time";
import { MultiValueTypeInput } from "../multi-value-type-input/multi-value-type-input";
import { Select } from "../select/select";
import { TagChipList } from "../tag-chip/tag-chip";
import { OverflowTooltip } from "../tooltip/tooltip";
import { CardIconType, TableAlignment, TableFieldType, TableRowCheckboxStyleOverrides } from "./table-constants";
import { Checkbox } from "../forms/form-checkbox";

function TableRowSpacer() {
    return (
        <td className="eone-ui-table-row-spacer"></td>
    );
}

function TableRowReorderHandle(props) {
    const getClass = () => {
        if (props.IsDragging) {
            return "eone-ui-table-row-drag-button-dragging";
        }
        return "eone-ui-table-row-drag-button";
    };

    return (
        <td {...props.DragHandle} className={getClass()} title="Drag to reorder">
            <FontAwesomeIcon icon={faGripDotsVertical} />
        </td>
    );
}

function TableRowButton(props) {
    const [isHovered, setIsHovered] = useState(false);

    const setHover = () => {
        setIsHovered(true);
    };

    const clearHover = () => {
        setIsHovered(false);
    };

    const onClick = (event) => {
        event.stopPropagation();
        props.OnClick();
    };

    const onDoubleClick = (event) => {
        event.stopPropagation();
    };

    const className = props.Cancel ? "eone-ui-row-button-danger" : "eone-ui-table-row-button";
    const icon = isHovered && props.HoverIcon ? props.HoverIcon : props.Icon;

    if (props.Hidden) {
        if (props.CollapseHidden) {
            return null;
        }
        return (
            <div className="eone-ui-table-row-button-hidden" />
        );
    }

    return (
        <button type="button" className={className} onClick={onClick} onDoubleClick={onDoubleClick} data-testid={props.DataTestId} onMouseOver={setHover} onMouseOut={clearHover} title={props.Title} aria-label={props.Title}>
            <FontAwesomeIcon icon={icon} />
        </button>
    );
}

function TableRowButtons(props) {
    const getButtons = () => {
        if (!props.Buttons || props.Buttons.length === 0) {
            return null;
        }

        // NOTE - Going to render the hidden row buttons in front so that the spacing is kept at the front
        const hiddenButtons = props.Buttons.filter((button) =>  button.Hidden && props.Row[button.Hidden])
        const visibleButtons = props.Buttons.filter((button) =>  !(button.Hidden && props.Row[button.Hidden]))

        return [...hiddenButtons, ...visibleButtons].map((button, index) => {
            const hidden = button.Hidden && props.Row[button.Hidden];
            return (
                <TableRowButton
                    key={index}
                    Icon={button.Icon}
                    HoverIcon={button.HoverIcon}
                    Title={button.Title}
                    Cancel={button.Cancel}
                    Hidden={hidden}
                    CollapseHidden={props.CollapseHidden}
                    DataTestId={getDataTestId(props.DataTestId, props.Row[props.IdField], button.Title)}
                    OnClick={() => { props.IdField ? button.OnClick(props.Row[props.IdField]) : button.OnClick() }} />
            );
        });
    };

    if (!props.Buttons || props.Buttons.length === 0) {
        return null;
    }

    const innerStyle = {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 10,
        display: 'flex',
        gap: 10,
        ...(props.FixedRowButtons
            ? { backgroundColor: 'var(--control-background-color)', borderLeft: '1px solid var(--surface-base-light)' }
            : {}),
    };

    return (
        <td className="eone-ui-table-row-button-group" style={{ width: props.Width }}>
            <div className="eone-ui-table-row-button-group-container">
                <div style={innerStyle}>
                    {getButtons()}
                </div>
            </div>
        </td>
    );
}

function TableRowItem(props) {
    const changeValueType = (valueType) => {
        if (typeof props.ChangeValue === "function") {
            props.ChangeValue(props.FieldValueType, valueType);
        }
    };

    const changeValue = (value) => {
        if (typeof props.ChangeValue === "function") {
            props.ChangeValue(props.FieldName, value);
        }
    };

    const updateValue = (value) => {
        if (typeof props.UpdateValue === "function") {
            props.UpdateValue(props.FieldName, value);
        }
    };

    const changeAndUpdateValue = (value) => {
        changeValue(value);
        updateValue(value);
    };

    const toggleCheckboxValue = () => {
        const oldValue = props.Row[props.FieldName];
        const newValue = !oldValue;
        changeValue(newValue);
        updateValue(newValue);
    };

    const getOptions = () => {
        if (props.Options) {
            if (Array.isArray(props.Options)) {
                return props.Options;
            }
            if (props.Options in props.Row) {
                return props.Row[props.Options];
            }
        }
        return [];
    };

    const getOptionValue = (options, key) => {
        for (let i = 0, len = options.length; i < len; i++) {
            if (options[i].Key === key) {
                return options[i].Value;
            }
        }
        return key;
    };

    const getSelectOption = (key) => {
        const options = getOptions();
        return options.find(opt => opt.Key === key) || null;
    };

    const getSelectLabelValue = (value) => {
        const options = getOptions();
        return getOptionValue(options, value);
    };

    const getDateLabelValue = (value, userTimeZone) => {
        if (!value) return "";
        const dateValue = new Date(value);
        const utcDateValue = new Date(Date.UTC(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), dateValue.getHours(), dateValue.getMinutes(), dateValue.getSeconds()));
        const options = { timeZone: userTimeZone || "UTC" };
        return utcDateValue.toLocaleDateString(props.Locale, options);
    };

    const getTimeLabelValue = (value, userTimeZone) => {
        if (!value) return "";
        const dateValue = new Date(value);
        const utcDateValue = new Date(Date.UTC(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), dateValue.getHours(), dateValue.getMinutes(), dateValue.getSeconds()));
        const options = { timeZone: userTimeZone || "UTC" };
        return utcDateValue.toLocaleTimeString(props.Locale, options);
    }

    const getPercentageLabelValue = (value) => {
        if (!value) return Number(0).toLocaleString(props.Locale, { style: 'percent', minimumFractionDigits: 2 });
        return Number(value).toLocaleString(props.Locale, { style: 'percent', minimumFractionDigits: 2 });
    };

    const getDateTimeLabelValue = (value, userTimeZone) => {
        if (!value) return null;
        const dateValue = new Date(value);
        const utcDateValue = new Date(Date.UTC(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), dateValue.getHours(), dateValue.getMinutes(), dateValue.getSeconds()));
        const options = { timeZone: userTimeZone || "UTC" };
        const datePart = utcDateValue.toLocaleDateString(props.Locale, options);
        const timePart = utcDateValue.toLocaleTimeString(props.Locale, options);
        return datePart + " " + timePart;
    };

    const getLabelValue = (value, isArray, userTimeZone) => {
        if (isArray) {
            if (!value) return "";
            let labels = [];
            for (let i = 0, len = value.length; i < len; i++) {
                labels.push(getLabelValue(value[i], false));
            }
            labels.sort();
            return labels.join(", ");
        }
        switch (props.FieldType) {
            case TableFieldType.Checkbox:
                return value ? "Yes" : "No";
            case TableFieldType.Select:
                return getSelectLabelValue(value);
            case TableFieldType.Date:
                return getDateLabelValue(value, userTimeZone);
            case TableFieldType.Time:
                return getTimeLabelValue(value, userTimeZone);
            case TableFieldType.DateTime:
                return getDateTimeLabelValue(value, userTimeZone);
            case TableFieldType.Percent:
                return getPercentageLabelValue(value);
            default:
                return value;
        }
    };

    const getClass = () => {
        let className = "eone-ui-table-row-data";
        switch (props.Alignment) {
            case TableAlignment.Center:
                className = `${className} eone-ui-table-row-data-center`;
                break;
            case TableAlignment.Right:
                className = `${className} eone-ui-table-row-data-right`;
                break;
        }
        return className;
    };

    const getFieldType = () => {
        if (props.FieldType) {
            if (Number.isInteger(props.FieldType)) {
                return props.FieldType;
            }
            if (props.FieldType in props.Row) {
                return props.Row[props.FieldType];
            }
        }
        return TableFieldType.String;
    };

    const getValueTypes = () => {
        return props.ValueTypes || [];
    };

    if (props.Visible === false) {
        return (
            <td className="eone-ui-table-row-data" />
        );
    }

    const value = props.Row[props.FieldName];
    const fieldType = getFieldType();

    if (fieldType === TableFieldType.Tags) {
        return (
            <td className="eone-ui-table-row-data" style={{ width: props.Width }}>
                <TagChipList Tags={value} />
            </td>
        );
    }

    if (fieldType === TableFieldType.MultiValue) {
        const valueTypes = getValueTypes();
        const valueType = props.Row[props.FieldValueType];
        return (
            <td className="eone-ui-table-row-data" style={{ width: props.Width }}>
                <MultiValueTypeInput
                    ReadOnly={!props.Editable}
                    ValueTypes={valueTypes}
                    ValueType={valueType}
                    Value={value}
                    ContainerClass="eone-ui-input-table"
                    ValidChars={props.ValidChars}
                    InvalidChars={props.InvalidChars}
                    Validate={props.Validate}
                    Placeholder={props.Placeholder}
                    ChangeValueType={changeValueType}
                    ChangeValue={changeValue}
                    UpdateValue={updateValue} />
            </td>
        );
    }

    if (fieldType === TableFieldType.Color) {
        return (
            <td className="eone-ui-table-row-data">
                <ColorPicker
                    ReadOnly={!props.Editable}
                    Value={value}
                    ContainerClass="eone-ui-input-table"
                    OnChange={changeAndUpdateValue} />
            </td>
        );
    }

    if (props.Editable) {
        switch (fieldType) {
            case TableFieldType.Checkbox:
                return (
                    <td className="eone-ui-table-row-data" style={{ width: props.Width }}>
                        <Checkbox
                            Center
                            Style={TableRowCheckboxStyleOverrides}
                            OnChange={toggleCheckboxValue}
                            Value={value} />
                    </td>
                );
            case TableFieldType.Select:
                return (
                    <td className="eone-ui-table-row-data" style={{ width: props.Width }}>
                        <Select
                            Placeholder={props.Placeholder}
                            MultiSelect={props.Array}
                            Options={getOptions()}
                            Value={value}
                            ContainerClass="eone-ui-input-table-select"
                            Search={props.ShowSearch}
                            Loading={props.Loading}
                            OnChange={changeAndUpdateValue} />
                    </td>
                );
            case TableFieldType.String:
                return (
                    <td className="eone-ui-table-row-data" style={{ width: props.Width }}>
                        <OverflowTooltip Label={value}>
                            <InputString
                                Value={value}
                                Title=""
                                ContainerClass="eone-ui-input-table"
                                ValidChars={props.ValidChars}
                                Placeholder={props.Placeholder}
                                InvalidChars={props.InvalidChars}
                                Validate={props.Validate}
                                OnChange={changeValue}
                                OnBlur={updateValue} />
                        </OverflowTooltip>
                    </td>
                );
            case TableFieldType.Integer:
                return (
                    <td className="eone-ui-table-row-data" style={{ width: props.Width }}>
                        <InputNumber
                            Value={value}
                            ContainerClass="eone-ui-input-table"
                            Locale={props.Locale}
                            Placeholder={props.Placeholder}
                            MaximumValue={props.MaximumValue}
                            MinimumValue={props.MinimumValue}
                            AllowNegative={props.AllowNegative}
                            Validate={props.Validate}
                            OnChange={changeValue}
                            OnBlur={updateValue} />
                    </td>
                );
            case TableFieldType.Date:
                return (
                    <td className="eone-ui-table-row-data" style={{ width: props.Width }}>
                        <DatePicker
                            Value={value}
                            ContainerClass="eone-ui-input-table-date"
                            FixedMenu
                            Locale={props.Locale}
                            OnChange={changeAndUpdateValue} />
                    </td>
                );
            case TableFieldType.Time:
                return (
                    <td className="eone-ui-table-row-data" style={{ width: props.Width }}>
                        <InputTime
                            Value={value}
                            ContainerClass="eone-ui-input-table-date"
                            FixedMenu
                            Locale={props.Locale}
                            OnChange={changeAndUpdateValue} />
                    </td>
                );
        }
    }

    const label = getLabelValue(value, props.Array, props.UserTimeZone);
    if (fieldType === TableFieldType.Select) {
        const option = getSelectOption(value);
        const icon = option?.Icon;
        if (icon) {
            const isAppFont = option.IconType === CardIconType.AppFontIcon || option.IconType === "af";
            const iconElement = isAppFont
                ? <AppFont Icon={icon} />
                : <FontAwesomeIcon icon={icon} />;
            return (
                <OverflowTooltip Label={label}>
                    <td className={getClass()}>
                        <div>
                            <span className="eone-ui-select-icon">{iconElement}</span>
                            <span>{label}</span>
                        </div>
                    </td>
                </OverflowTooltip>
            );
        }
    }
    return (
        <OverflowTooltip Label={label}>
            <td className={getClass()}>{label}</td>
        </OverflowTooltip>
    );
}

function TableRow(props) {
    const {
        attributes,
        listeners,
        setNodeRef,
        isDragging,
        transform,
        transition
    } = useSortable({
        id: props.Row[props.IdField],
        transition: { duration: 25, easing: "cubic-bezier(1, 1, 0.5, 1)" },
    });

    const changeValue = (fieldName, value) => {
        if (typeof props.ChangeValue === "function") {
            props.ChangeValue(props.Row[props.IdField], fieldName, value);
        }
    };

    const updateValue = (fieldName, value) => {
        if (typeof props.UpdateValue === "function") {
            props.UpdateValue(props.Row[props.IdField], fieldName, value);
        }
    };

    const selectRow = () => {
        if (typeof props.SelectRow === "function") {
            props.SelectRow(props.Row[props.IdField]);
        }
    };

    const doubleClickRow = (event) => {
        if (typeof props.OnDoubleClick !== "function") return;
        if (event.target.closest("input, select, textarea, button, .eone-ui-table-row-button-group")) return;
        props.OnDoubleClick(props.Row[props.IdField]);
    };

    const getClass = () => {
        if (transform) {
            if (isDragging) {
                return "eone-ui-table-row eone-ui-table-row-draggable eone-ui-table-row-dragging";
            } else {
                return "eone-ui-table-row eone-ui-table-row-draggable";
            }
        }

        if (props.Selected) {
            return "eone-ui-table-row eone-ui-table-row-selected";
        }
        return "eone-ui-table-row";
    };

    const isColumnSelected = (column, selectedColumns) => {
        if (selectedColumns && selectedColumns.length > 0 && !selectedColumns.includes(column.FieldName) && !column.Locked) {
            return false;
        }
        return column.Visible === undefined || column.Visible === true;
    };

    const getSelectedColumns = () => {
        let columns = [];
        for (let i = 0, len = props.Columns.length; i < len; i++) {
            if (isColumnSelected(props.Columns[i], props.SelectedColumns)) {
                columns.push(props.Columns[i]);
            }
        }
        return columns;
    };

    const getRenderRowCellClass = (alignment) => {
        let className = "eone-ui-table-row-data";
        if (alignment === TableAlignment.Center) className += " eone-ui-table-row-data-center";
        if (alignment === TableAlignment.Right) className += " eone-ui-table-row-data-right";
        return className;
    };

    const getItems = () => {
        if (!props.Columns) return null;
        const columns = getSelectedColumns();
        const defaultWidth = `${100 / columns.length}%`;

        const getWidth = (column) => {
            if (props.GetColumnWidth) {
                const resizedWidth = props.GetColumnWidth(column.FieldName);
                if (resizedWidth) return resizedWidth;
            }
            return column.Width || defaultWidth;
        };

        return columns.map((column, index) => {
            if (typeof column.RenderRow === "function") {
                const cellWidth = column.Width || props.Width;
                return (
                    <td
                        key={index}
                        className={getRenderRowCellClass(column.Alignment)}
                        style={{ width: cellWidth }}
                    >
                        {column.RenderRow({ ColumnIndex: index, Row: props.Row, Column: column })}
                    </td>
                );
            }

            const visible = column.CellVisible && props.Row[column.CellVisible];
            const fieldType = column.FieldType || props.Row[column.RowFieldType] || TableFieldType.String;
            const options = column.Options || props.Row[column.RowOptions] || [];
            const valueTypes = column.ValueTypes || props.Row[column.RowValueTypes] || [];
            const maximumValue = column.MaximumValue || props.Row[column.RowMaximumValue];
            const minimumValue = column.MinimumValue || props.Row[column.RowMinimumValue];
            const editable = column.RowEditable !== undefined ? props.Row[column.RowEditable] : column.Editable;
            return (
                <TableRowItem
                    key={column.FieldName}
                    Width={getWidth(column)}
                    FieldName={column.FieldName}
                    FieldValueType={column.FieldValueType}
                    FieldType={fieldType}
                    Placeholder={column.Placeholder}
                    ValidChars={column.ValidChars}
                    InvalidChars={column.InvalidChars}
                    MaximumValue={maximumValue}
                    MinimumValue={minimumValue}
                    Validate={column.Validate}
                    Array={column.Array}
                    UserTimeZone={column.UserTimeZone}
                    Alignment={column.Alignment}
                    ValueTypes={valueTypes}
                    Options={options}
                    ShowSearch={column.ShowSearch}
                    Loading={column.Loading}
                    Editable={editable}
                    Row={props.Row}
                    Visible={visible}
                    Locale={props.Locale}
                    ChangeValue={changeValue}
                    UpdateValue={updateValue}
                    DataTestId={props.DataTestId} />
            );
        });
    };

    const getStyle = () => {
        if (transform) {
            return {
                transition,
                transform: CSS.Translate.toString(transform)
            }
        }
        return null;
    };

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    if (props.Reorderable) {
        return (
            <tr className={getClass()} style={getStyle()} onClick={selectRow} onDoubleClick={doubleClickRow} ref={setNodeRef}>
                <TableRowReorderHandle DragHandle={{ ...attributes, ...listeners }} IsDragging={isDragging} />
                {getItems()}
                <TableRowButtons
                    Buttons={props.Buttons}
                    Row={props.Row}
                    IdField={props.IdField}
                    DataTestId={props.DataTestId}
                    CollapseHidden={props.CollapseHiddenButtons}
                    FixedRowButtons={props.FixedRowButtons}
                    Width={props.ButtonWidth} />
                <TableRowSpacer />
            </tr>
        );
    }

    return (
        <tr className={getClass()} onClick={selectRow} onDoubleClick={doubleClickRow}>
            <TableRowSpacer />
            {getItems()}
            <TableRowButtons
                Buttons={props.Buttons}
                Row={props.Row}
                IdField={props.IdField}
                DataTestId={props.DataTestId}
                CollapseHidden={props.CollapseHiddenButtons}
                FixedRowButtons={props.FixedRowButtons}
                Width={props.ButtonWidth} />
            <TableRowSpacer />
        </tr>
    );
}

export { TableRow };